import type { ConnectorOverview } from "@/lib/api";
import { i18n } from "@/lib/i18n";

/**
 * Where the dashboard runs, from `GET /mcp/overview`: the backend's Railway
 * environment inside a Railway deployment ("production", "dev"; trimmed and
 * lower case), or "local" anywhere else, such as a developer's machine, and
 * whether it may write to Odoo and the other systems it reads. Only a
 * deployment in this project's Railway production environment ever writes to
 * them; every other place is read-only, whatever is set, and EXTERNAL_WRITES
 * can switch production off too.
 */
export type DeploymentFacts = Pick<ConnectorOverview, "environment" | "external_writes">;

/** The environment that shows nothing new: the one De Ceuster uses. */
export const PRODUCTION_ENVIRONMENT = "production";

/** What the environment badge says. */
export interface EnvironmentBadgeFacts {
  /** The environment's name with a capital: "Dev", "Staging". */
  name: string;
  /** True when the environment never writes to Odoo or any other system. */
  readOnly: boolean;
  /** What the badge means, in a sentence: its tooltip. */
  title: string;
}

/** An environment's name as the badge shows it: "dev" becomes "Dev", "staging" "Staging". */
export function environmentName(environment: string): string {
  const value = environment.trim().toLowerCase();
  return value.charAt(0).toUpperCase() + value.slice(1);
}

/**
 * The badge that names an environment other than production, in the
 * dashboard's language, or null: in production, and while the overview loads
 * or after it failed, so production never shows anything new. A backend that
 * does not say its environment yet shows nothing either.
 */
export function environmentBadge(facts: DeploymentFacts | undefined): EnvironmentBadgeFacts | null {
  // `??`: a backend from before these fields, for the moment both deploy.
  const environment = (facts?.environment ?? "").trim().toLowerCase();
  if (!facts || environment === "" || environment === PRODUCTION_ENVIRONMENT) {
    return null;
  }
  const name = environmentName(environment);
  const readOnly = facts.external_writes !== true;
  return {
    name,
    readOnly,
    title: i18n.t(readOnly ? "environment.titleReadOnly" : "environment.title", { name }),
  };
}
